'use client';

import { use, type ReactElement } from 'react';
import Image from 'next/image';
import { Building2 } from 'lucide-react';
import { QueryClientContext } from '@tanstack/react-query';
import { useController, type Control } from 'react-hook-form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { tokens } from '@/lib/design-tokens';
import type { Contact } from '@/lib/services/contacts/types';
import {
  usePmsContactTypeSelection,
  usePmsMatterContactBindings
} from '@/lib/services/integrations/pms/hooks';
import type {
  PmsContactTypeOption,
  PmsProvider
} from '@/lib/services/integrations/pms/types';
// Imported directly, not via the `pms` barrel — several suites mock that barrel with a partial
// factory, which resolves this registry to `undefined` at module-init time. See its own header.
import {
  getPmsProviderPresentationFor,
  type PmsProviderPresentation
} from '@/lib/services/integrations/pms/provider-presentation';
import { PmsContactTypeRefreshButton } from './PmsContactTypeRefreshButton';

/**
 * The support caveat rendered under the picker.
 *
 * **Required, not decoration.** ActionStep validates contact types per action type server-side, and
 * the endpoint that would tell us which types are legal for a given matter type
 * (`actiontypeparticipanttypes`) is unreachable on our app registration — so the list offered here is
 * the firm's *whole* catalogue and cannot be filtered to the valid subset. Disclosure is the chosen
 * mitigation for that: the picker must not read as authoritative.
 */
export const PMS_CONTACT_TYPE_SUPPORT_NOTE =
  'The selected Participant type must be supported by your Action type in order to sync correctly.';

/**
 * What stands in for the caveat while the firm's catalogue has never been synced (VALE-587).
 *
 * **This state is reachable at all only because the picker's gate widened.** It renders when the PMS
 * publishes a catalogue but nothing has yet been read from it — `syncedAt === null`, the row
 * {@link publishesContactTypeCatalogue} exists to keep apart from "this provider publishes none".
 * Before VALE-587 the picker was gated on `contactTypes.length > 0` and simply vanished here, while
 * the refresh that populates it sat on the integration card; moving the refresh to the picker without
 * widening the gate would have hidden the remedy in the one situation it was built for.
 *
 * Exported so the drawer's attribute section says the same thing — the two surfaces must not drift
 * into describing one state two ways.
 */
export const PMS_CONTACT_TYPE_EMPTY_CATALOGUE_NOTE =
  'No participant types have been read from your practice management system yet. Refresh to pull them now.';

/**
 * The PMS's brand mark, or a neutral icon for a provider with no asset.
 *
 * Mirrors the import modal's `ProviderMark`: a provider whose mark we do not have renders a neutral
 * icon rather than a fabricated one, since inventing another company's mark misrepresents them.
 *
 * **Exported** so the Contacts drawer's attribute path can head its ActionStep section with the same
 * mark (VALE-158) instead of hand-rolling a look-alike — the fallback branch is the part worth
 * having one definition of, since a second copy is exactly where a fabricated mark creeps back in.
 */
export function ProviderMark({
  logoUrl,
  label,
  size = 16
}: {
  logoUrl?: string;
  label: string;
  /** Square edge in px. 14 suits a 12px uppercase section heading; 16 a body-text row. */
  size?: number;
}): ReactElement {
  if (!logoUrl) {
    return (
      <Building2
        className="text-muted-foreground shrink-0"
        style={{ width: size, height: size }}
        aria-hidden
      />
    );
  }

  return (
    <Image
      src={logoUrl}
      alt={label}
      width={size}
      height={size}
      className="shrink-0 rounded-xs"
    />
  );
}

/**
 * The edit control, split out so its two `useController` subscriptions are only mounted once the
 * gate above has decided there is a catalogue to offer — hooks cannot sit behind an early return.
 */
function PmsContactTypeField({
  control,
  contactTypes,
  presentation,
  provider,
  matterId,
  contactId
}: {
  control: Control<Partial<Contact>>;
  contactTypes: PmsContactTypeOption[];
  presentation: PmsProviderPresentation;
  provider: PmsProvider;
  matterId?: number;
  contactId?: number;
}): ReactElement {
  // The contact's already-stored selection, from the matter-scoped bindings read — the same source
  // `PmsContactBindingRow` displays, deliberately, so the picker and the row can never disagree
  // about what this contact is filed under.
  //
  // Read here rather than in the gate above so it never runs for a firm with no catalogue, which is
  // most of them. `enabled`-gated on there being a matter: during matter creation the contact does
  // not exist yet, so there is nothing stored to seed from.
  const { data: bindings } = usePmsMatterContactBindings(
    provider,
    matterId,
    !!matterId
  );

  // Shown until the user picks something else. Deliberately NOT seeded into the form's
  // `defaultValues`: the field must stay **absent from the submitted payload** unless the user
  // actually chooses, because an untouched save that re-asserted the selection is exactly what would
  // let an unrelated edit disturb a live binding.
  const storedTypeId =
    bindings?.find((binding) => binding.contactId === contactId)
      ?.pmsContactTypeId ?? null;
  const { field: idField, fieldState } = useController({
    control,
    name: 'pmsContactTypeId'
  });
  const { field: labelField } = useController({
    control,
    name: 'pmsContactTypeLabel'
  });

  // The label travels with the id, written at the one point both halves are in hand. Storing it is
  // what lets an existing selection render without a catalogue call — and therefore without the PMS
  // being reachable at all.
  const hasContactTypes = contactTypes.length > 0;

  const handleChange = (externalId: string): void => {
    idField.onChange(externalId);
    labelField.onChange(
      contactTypes.find((type) => type.externalId === externalId)?.name ?? null
    );
  };

  return (
    <div className={tokens.patterns.form.spacing}>
      {/*
        The refresh sits beside the label rather than in the field, so it reads as an action on the
        *list* rather than on the contact's selection — and stays reachable when the list is empty
        and the trigger below is disabled, which is the case it exists for.
      */}
      <div className="flex items-center justify-between gap-2">
        <label
          htmlFor="pmsContactTypeId"
          className={tokens.patterns.form.label}
        >
          <span className="flex items-center gap-1.5">
            <ProviderMark
              logoUrl={presentation.logoUrl}
              label={presentation.label}
            />
            {presentation.label} contact type
          </span>
        </label>

        <PmsContactTypeRefreshButton provider={provider} />
      </div>

      <Select
        value={(idField.value as string) || storedTypeId || ''}
        onValueChange={handleChange}
        /*
          Nothing to open when the catalogue has never been synced. A `Select` with no items still
          opens — onto an empty popover — which reads as a broken control rather than as "there is
          nothing here yet"; the placeholder and the note below say that instead.
        */
        disabled={!hasContactTypes}
      >
        <SelectTrigger id="pmsContactTypeId">
          <SelectValue
            placeholder={
              hasContactTypes
                ? 'Select contact type...'
                : 'No participant types yet'
            }
          />
        </SelectTrigger>
        <SelectContent>
          {contactTypes.map((type) => (
            <SelectItem key={type.externalId} value={type.externalId}>
              {type.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {fieldState.error?.message && (
        <span className="text-destructive text-sm">
          {fieldState.error.message}
        </span>
      )}

      {/*
        The support caveat describes a limitation *of the offered list*, so with no list yet it is a
        non-sequitur — it would warn the reader their choices might be incomplete before they have
        been given any. The prompt that replaces it names the remedy sitting beside the label.
      */}
      <span className="text-muted-foreground text-xs">
        {hasContactTypes
          ? PMS_CONTACT_TYPE_SUPPORT_NOTE
          : PMS_CONTACT_TYPE_EMPTY_CATALOGUE_NOTE}
      </span>
    </div>
  );
}

export interface PmsContactTypeSelectorProps {
  control: Control<Partial<Contact>>;
  mode: 'read' | 'edit';
  values: Partial<Contact>;
  /**
   * The matter this contact is being edited on, when there is one.
   *
   * Present ⇒ the picker seeds itself from the contact's stored selection on that matter, so opening
   * the card shows what was already chosen. Absent is the matter-creation case: the contact does not
   * exist yet, so there is nothing stored to seed from and the picker starts empty. The selector's
   * own visibility gate is firm-level and does **not** depend on this — which is what lets one
   * component serve both surfaces.
   */
  matterId?: number;
}

/**
 * Choose which PMS contact type this contact is filed under on this matter.
 *
 * **Not `ContactTypeSelector`** — that one, in this same directory, means Individual vs Organisation
 * and is a property of the person. This is a per-`(contact, matter)` filing decision in the firm's
 * practice-management system, and the two must never be conflated.
 *
 * **Vale never chooses a type.** There is no default and no fallback: a contact with no selection is
 * simply not filed on the PMS matter. The behaviour this replaces — filing every non-client contact
 * as `Individual` — is the defect, since `Individual` is a *base type* describing what a participant
 * is rather than a role they hold, and ActionStep rejects it on most action types.
 *
 * Rendered only when the firm's active PMS **publishes** a catalogue (see
 * {@link usePmsContactTypeSelection}) — a firm-level gate, so it behaves identically when editing a
 * contact on an existing matter and when creating one during matter creation, where no persisted
 * matter exists to ask about.
 *
 * **The gate is `publishesCatalogue`, not `isAvailable`, since VALE-587.** The two differ by exactly
 * one case — a catalogue nobody has asked the provider for yet (`syncedAt === null`, an empty list) —
 * and that case is precisely what the refresh beside the label exists to fix. Gating on a non-empty
 * list would hide the control in the one situation it was built for, which is why the refresh could
 * previously only live on the integration card. Smokeball is still excluded: it answers with a
 * *synced* empty catalogue, which `publishesContactTypeCatalogue` reads as "publishes none".
 */
export function PmsContactTypeSelector(
  props: PmsContactTypeSelectorProps
): ReactElement | null {
  // `ContactForm` is rendered in trees that have no React Query provider — component-test harnesses
  // for the AI-review and intake-preview cards, and Storybook stories without the provider
  // decorator. Asking "does this firm have a PMS?" is impossible there, so the selector degrades to
  // absent rather than throwing "No QueryClient set" and taking those suites down with it. Read via
  // `use(QueryClientContext)` rather than by calling the query hooks, because those cannot be called
  // conditionally — hence the split into `PmsContactTypeSelectorWithQuery` below.
  //
  // Fail-closed is also the honest answer: "cannot determine the firm's PMS" and "the firm has no
  // catalogue" should look identical, and both mean no selector.
  const queryClient = use(QueryClientContext);

  if (!queryClient) {
    return null;
  }

  return <PmsContactTypeSelectorWithQuery {...props} />;
}

function PmsContactTypeSelectorWithQuery({
  control,
  mode,
  values,
  matterId
}: PmsContactTypeSelectorProps): ReactElement | null {
  const { provider, contactTypes, publishesCatalogue } =
    usePmsContactTypeSelection();

  if (!publishesCatalogue || !provider) {
    return null;
  }

  // Read mode renders nothing on purpose: `PmsContactBindingRow` (VALE-141) already shows the stored
  // type and its bind outcome on exactly this surface. A second read-only copy of the same fact,
  // from the same query, would be redundant on the card and would drift the day one of them changed.
  if (mode === 'read') {
    return null;
  }

  const presentation = getPmsProviderPresentationFor(provider);

  return (
    <PmsContactTypeField
      control={control}
      contactTypes={contactTypes}
      presentation={presentation}
      provider={provider}
      matterId={matterId}
      contactId={values.id}
    />
  );
}
